import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Form,
  InputGroup
} from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { BsChevronLeft } from "react-icons/bs";
import iconSubscription from "../../images/icon-subscribe.png";
import Header from "../../components/Header";
import AppConstants from "../../AppConstants";
import useAuthToken from "../../hooks/useAuthToken";
import "./style.scss";

interface PlanData {
  basePrice: number;
  pricePerUser: number;
  currentUsers: number;
  maxUsers: number;
  planName: string;
}

// Function to fetch pricing by licenses
const fetchPricingByLicenses = async (
  licenses: number,
  setApiPricingData: any,
  token: string | null
) => {
  try {
    const url = `${AppConstants.PRICING_BY_LICENSES_API}?licenses=${licenses}`;
    const headers: Record<string, string> = {
      "Content-Type": "application/json"
    };

    // Add Authorization header if token exists
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Store the API response data in state
    setApiPricingData(data);

    return data;
  } catch (error) {
    console.error("Error fetching pricing by licenses:", error);
    throw error;
  }
};

// Function to redeem promocode
const redeemPromocode = async (promocode: string, token: string | null) => {
  try {
    const url = AppConstants.PROMOCODE_REDEEM_API;
    const headers: Record<string, string> = {
      "Content-Type": "application/json"
    };

    // Add Authorization header if token exists
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({
        code: promocode.trim()
      })
    });

    const data = await response.json();
    console.log("Promocode Redeem API Response:", data);

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  } catch (error) {
    console.error("Error redeeming promocode:", error);
    throw error;
  }
};

const CreatePlan: React.FC = () => {
  const navigate = useNavigate();
  const { userToken, ready, getUserIdFromToken } = useAuthToken();

  // Static plan data
  const [planData] = useState<PlanData>({
    basePrice: 4.0,
    pricePerUser: 0.95,
    currentUsers: 4,
    maxUsers: 20,
    planName: "Standard Plan"
  });

  const [selectedUsers, setSelectedUsers] = useState(1);
  const [showWarning, setShowWarning] = useState(false);
  const [isPricingLoading, setIsPricingLoading] = useState(false);
  const [promocode, setPromocode] = useState("");
  const [promocodeApplied, setPromocodeApplied] = useState(false);
  const [promocodeDiscount, setPromocodeDiscount] = useState(0);
  const [promocodeMessage, setPromocodeMessage] = useState("");
  const [promocodeMessageType, setPromocodeMessageType] = useState<
    "success" | "error" | ""
  >("");
  const [autoRenewal, setAutoRenewal] = useState(true);
  const [apiPricingData, setApiPricingData] = useState<any>(null);

  // Call API on component mount with default licenses count of 1
  useEffect(() => {
    const fetchInitialPricing = async () => {
      if (!ready) return;

      setIsPricingLoading(true);
      try {
        await fetchPricingByLicenses(1, setApiPricingData, userToken);
      } catch (error) {
        console.error("Failed to fetch initial pricing:", error);
      } finally {
        setIsPricingLoading(false);
      }
    };

    fetchInitialPricing();
  }, [ready, userToken]); // Wait for auth hook to be ready

  const handleUserChange = async (increment: boolean) => {
    setIsPricingLoading(true);

    if (increment && selectedUsers < planData.maxUsers) {
      const newUserCount = selectedUsers + 1;
      setSelectedUsers(newUserCount);
      setShowWarning(false);

      // Call pricing API when incrementing
      try {
        await fetchPricingByLicenses(
          newUserCount,
          setApiPricingData,
          userToken
        );
      } catch (error) {
        console.error("Failed to fetch pricing:", error);
        // You could show a toast notification here if needed
        // toast.error('Failed to fetch pricing information');
      }
    } else if (!increment && selectedUsers > 1) {
      const newUserCount = selectedUsers - 1;
      setSelectedUsers(newUserCount);

      // Show warning if reducing below current users
      if (newUserCount < planData.currentUsers) {
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }

      // Call pricing API when decrementing
      try {
        await fetchPricingByLicenses(
          newUserCount,
          setApiPricingData,
          userToken
        );
      } catch (error) {
        console.error("Failed to fetch pricing:", error);
        // You could show a toast notification here if needed
        // toast.error('Failed to fetch pricing information');
      }
    }

    setIsPricingLoading(false);
  };

  const calculatePrice = () => {
    // Destructure API pricing data for cleaner access
    const unitAmount = apiPricingData?.pricingConfig?.tier?.unitAmount;

    // Use API pricing data if available
    if (unitAmount !== undefined) {
      return unitAmount || 0;
    }

    // Fallback to static calculation
    if (selectedUsers <= planData.currentUsers) {
      return 0; // No additional cost for reducing users
    }
    return (selectedUsers - planData.currentUsers) * planData.pricePerUser;
  };

  const calculateTotalPrice = () => {
    // Destructure API pricing data for cleaner access
    const unitAmount = apiPricingData?.pricingConfig?.tier?.unitAmount;

    // Use API pricing data if available
    if (unitAmount !== undefined) {
      const baseAmount = unitAmount || 0;
      return baseAmount - promocodeDiscount;
    }

    // Fallback to static calculation
    const baseTotal =
      planData.basePrice +
      (selectedUsers - planData.currentUsers) * planData.pricePerUser;
    return baseTotal - promocodeDiscount;
  };

  const handleApplyPromocode = async () => {
    if (promocode.trim() === "") {
      setPromocodeMessage("Please enter a promocode");
      setPromocodeMessageType("error");
      return;
    }

    // Clear previous messages
    setPromocodeMessage("");
    setPromocodeMessageType("");

    try {
      const response = await redeemPromocode(promocode, userToken);

      // Handle successful response
      if (response.success) {
        // Extract discount amount from the response
        const discountAmount =
          response.discountAmount || response.discount || 0;

        setPromocodeDiscount(discountAmount);
        setPromocodeApplied(true);
        setPromocodeMessage(
          response.message || "Promocode applied successfully!"
        );
        setPromocodeMessageType("success");
      } else {
        // Handle API returning success: false
        setPromocodeMessage(response.message || "Invalid promocode");
        setPromocodeMessageType("error");
      }
    } catch (error: any) {
      // Handle API errors
      const errorMessage = error.message || "Failed to apply promocode";
      setPromocodeMessage(errorMessage);
      setPromocodeMessageType("error");
      console.error("Promocode redemption failed:", error);
    }
  };

  const getDurationText = () => {
    const additionalUsers = selectedUsers - planData.currentUsers;
    if (additionalUsers <= 0) return "0 days";
    return "0 days"; // Prorated billing
  };

  const handleContinueToPayment = () => {
    // Prepare plan data to pass to PaymentMethods component
    const planDataToPass = {
      customerId: getUserIdFromToken(), // Get from enhanced auth hook
      planName: apiPricingData?.pricingConfig?.tier?.name,
      priceId: apiPricingData?.pricingConfig?.tier?.stripePriceId,
      maxUsers: selectedUsers,
      promocode: promocode.trim(),
      autoRenew: autoRenewal,
      totalPrice: calculateTotalPrice()
    };

    console.log("Plan data being passed:", planDataToPass);

    // Navigate to payment methods with plan data
    navigate("/payment-methods", { state: planDataToPass });
  };

  const handleCancel = () => {
    navigate("/plan-details");
  };

  // Show loading state until auth is ready
  if (!ready) {
    return (
      <div className="upgrade-plan-page">
        <Header title="Subscription Plan" showMenu={true} />
        <Container className="px-0 py-3">
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Initializing...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="upgrade-plan-page">
      <Header
        title="Subscription Plan"
        showMenu={true}
        // currentPage="subscription-plan"
      />
      <Container className="px-0 py-3">
        <Row className="mb-4 justify-content-md-center">
          <Col xl={10} lg={10} md={12} sm={12}>
            <Row className="align-items-center my-4">
              <Col xs="auto" className="px-4">
                <Button
                  variant="link"
                  className="p-0 text-dark d-flex align-items-center"
                  onClick={() => navigate("/plan-details")}
                  style={{ fontSize: "16px", textDecoration: "none" }}
                >
                  <BsChevronLeft className="me-1" />
                  Back
                </Button>
              </Col>
            </Row>

            {/* Warning Alert */}
            {/* {showWarning && (
              <Alert variant="danger" className="mb-4">
                You need to add more users to receive a greater discount
              </Alert>
            )} */}

            {/* Plan Card */}
            <Card
              className="plan-card plan-upgrade-card"
              style={{ borderRadius: "16px", border: "none" }}
            >
              <Card.Body
                className="p-4"
                style={{ backgroundColor: "#fff", borderRadius: "16px" }}
              >
                {/* User Selection Section */}
                <Card
                  className="user-selection-card mb-4"
                  style={{
                    background:
                      "linear-gradient(65.07deg, #08A45D 1.12%, #0BBE6D 98.88%)",
                    border: "none",
                    borderRadius: "12px"
                  }}
                >
                  <Card.Body className="p-4">
                    <h5 className="text-center mb-4" style={{ color: "white" }}>
                      Number of Users
                    </h5>

                    <div className="d-flex align-items-center justify-content-center mb-4">
                      <Button
                        variant="outline-light"
                        className="rounded-circle counter-button"
                        style={{
                          width: "50px",
                          height: "50px",
                          fontSize: "20px",
                          borderColor: "white",
                          color: "white",
                          backgroundColor: "transparent"
                        }}
                        onFocus={(e) => e.target.blur()}
                        onClick={() => handleUserChange(false)}
                        disabled={selectedUsers <= 1 || isPricingLoading}
                      >
                        −
                      </Button>

                      <div
                        className="mx-4 px-4 py-2 text-center"
                        style={{
                          fontSize: "32px",
                          fontWeight: "bold",
                          minWidth: "80px",
                          color: "white"
                        }}
                      >
                        {selectedUsers}
                      </div>

                      <Button
                        variant="outline-light"
                        className="rounded-circle counter-button"
                        style={{
                          width: "50px",
                          height: "50px",
                          fontSize: "20px",
                          borderColor: "white",
                          color: "white",
                          backgroundColor: "transparent"
                        }}
                        onFocus={(e) => e.target.blur()}
                        onClick={() => handleUserChange(true)}
                        disabled={
                          selectedUsers >= planData.maxUsers || isPricingLoading
                        }
                      >
                        +
                      </Button>
                    </div>

                    {/* Price Display */}
                    <div className="text-center mb-3">
                      <div
                        className="price-display d-inline-block px-4 py-2"
                        style={{
                          backgroundColor: "white",
                          borderRadius: "20px",
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#333",
                          minHeight: "50px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        {isPricingLoading ? (
                          <div
                            className="spinner-border"
                            role="status"
                            style={{
                              color: "#28a745",
                              width: "1.5rem",
                              height: "1.5rem"
                            }}
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          `$${calculatePrice().toFixed(2)}/month`
                        )}
                      </div>
                    </div>

                    <p className="text-center mb-3" style={{ color: "white" }}>
                      You need to add more users to receive a greater discount.
                    </p>
                  </Card.Body>
                </Card>

                {/* Promocode Section */}
                <Card
                  className="mb-4"
                  style={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    border: "1px solid #e0e0e0"
                  }}
                >
                  <Card.Body className="p-4">
                    <h6
                      className="mb-3"
                      style={{ color: "#666", fontWeight: "600" }}
                    >
                      Promocode
                    </h6>
                    <InputGroup className="mb-2">
                      <Form.Control
                        type="text"
                        placeholder="Enter promocode"
                        value={promocode}
                        onChange={(e) => {
                          setPromocode(e.target.value);
                          // Clear validation messages when user starts typing
                          if (promocodeMessage) {
                            setPromocodeMessage("");
                            setPromocodeMessageType("");
                          }
                        }}
                        style={{
                          borderRadius: "8px 0 0 8px",
                          border: "1px solid #d0d0d0",
                          fontSize: "14px",
                          padding: "12px"
                        }}
                        disabled={promocodeApplied}
                      />
                      <Button
                        variant="success"
                        onClick={handleApplyPromocode}
                        disabled={promocodeApplied}
                        style={{
                          borderRadius: "0 8px 8px 0",
                          background:
                            "linear-gradient(65.07deg, #08A45D 1.12%, #0BBE6D 98.88%)",
                          border: "1px solid #08A45D",
                          padding: "12px 20px",
                          fontWeight: "600",
                          color: "white"
                        }}
                      >
                        Apply
                      </Button>
                    </InputGroup>

                    {/* Validation Messages */}
                    {promocodeMessage && (
                      <div
                        className={`text-${
                          promocodeMessageType === "success"
                            ? "success"
                            : "danger"
                        }`}
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          marginTop: "8px"
                        }}
                      >
                        {promocodeMessage}
                      </div>
                    )}
                  </Card.Body>
                </Card>

                {/* Price Breakdown */}
                <Card
                  className="mb-4"
                  style={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    border: "1px solid #e0e0e0"
                  }}
                >
                  <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span style={{ color: "#666", fontSize: "14px" }}>
                        Base Price
                      </span>
                      <span
                        style={{
                          color: "#333",
                          fontSize: "14px",
                          fontWeight: "500"
                        }}
                      >
                        $
                        {(
                          apiPricingData?.pricingConfig?.tier?.unitAmount ??
                          planData.basePrice
                        ).toFixed(2)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span style={{ color: "#666", fontSize: "14px" }}>
                        Promocode Discount
                      </span>
                      <span
                        style={{
                          color: "#333",
                          fontSize: "14px",
                          fontWeight: "500"
                        }}
                      >
                        -$0.00
                      </span>
                    </div>

                    {promocodeApplied && (
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span style={{ color: "#666", fontSize: "14px" }}>
                          Promocode Discount
                        </span>
                        <span
                          style={{
                            color: "#28a745",
                            fontSize: "14px",
                            fontWeight: "500"
                          }}
                        >
                          -${promocodeDiscount.toFixed(2)}
                        </span>
                      </div>
                    )}

                    <hr
                      style={{ margin: "12px 0", border: "1px solid #e0e0e0" }}
                    />

                    <div className="d-flex justify-content-between align-items-center">
                      <span
                        style={{
                          color: "#333",
                          fontSize: "18px",
                          fontWeight: "600"
                        }}
                      >
                        Total
                      </span>
                      <span
                        style={{
                          color: "#333",
                          fontSize: "24px",
                          fontWeight: "bold"
                        }}
                      >
                        ${calculateTotalPrice().toFixed(2)}
                      </span>
                    </div>

                    <div
                      style={{
                        color: "#666",
                        fontSize: "12px",
                        marginTop: "8px"
                      }}
                    >
                      You will be charged monthly until cancelled.
                    </div>
                  </Card.Body>
                </Card>

                {/* Auto-Renewal Section */}
                <Card
                  className="mb-4"
                  style={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    border: "1px solid #e0e0e0"
                  }}
                >
                  <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <span
                        style={{
                          color: "#333",
                          fontSize: "16px",
                          fontWeight: "600"
                        }}
                      >
                        Auto - Renewal
                      </span>
                      <Form.Check
                        type="switch"
                        id="auto-renewal-switch"
                        checked={autoRenewal}
                        onChange={(e) => setAutoRenewal(e.target.checked)}
                        style={{
                          fontSize: "18px"
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Card.Body>
            </Card>

            {/* Action Buttons */}
            <div className="m-4">
              <Button
                className="w-100 py-3 mb-3"
                style={{
                  background:
                    "linear-gradient(65.07deg, #08A45D 1.12%, #0BBE6D 98.88%)",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "white"
                }}
                onClick={handleContinueToPayment}
                disabled={isPricingLoading}
              >
                Continue to Payment
              </Button>

              <Button
                variant="link"
                className="w-100 text-success"
                onClick={handleCancel}
                style={{ fontSize: "16px" }}
              >
                Cancel
              </Button>

              {/* Back To App Button */}
              <div className="text-center mt-4 mb-3">
                <Button
                  variant="outline-success"
                  className="px-4 py-2"
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    borderColor: "#28a745",
                    color: "#28a745",
                    backgroundColor: "transparent"
                  }}
                  onMouseEnter={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.backgroundColor = "#28a745";
                    target.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.backgroundColor = "transparent";
                    target.style.color = "#28a745";
                  }}
                  onClick={() => {
                    window.location.replace("pretrip://subscription-success");
                  }}
                >
                  Back To App
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CreatePlan;
